import { Modal, Button, Alert, Table, Popconfirm } from 'antd';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';
import React, { useState } from 'react';
import { getClient } from '../../apollo';
import {
  ServiceSubtype,
  useVehicleTypesQuery,
  useDeleteVehiclePriceConditionMutation,
  useGetVehicleBrandModelsQuery,
} from '../../graphql/datamodel.gen';
import { VehiclePriceModal } from './vehiclePriceModal';

interface PriceTableModalProps {
  visible: boolean;
  onClose: () => void;
  subtype?: ServiceSubtype;
  refreshRate: () => any;
  clientId?: string;
  rateId: string;
}

export const PricesTableModal: React.FC<PriceTableModalProps> = (
  props: PriceTableModalProps,
) => {
  const [addVehiclePriceFilterVisible, setAddVehiclePriceFilterVisible] =
    useState(false);

  const [modifyVehiclePriceFilterVisible, setModifyVehiclePriceFilterVisible] =
    useState(false);
  const [vehiclePriceFilter, setVehiclePriceFilter] = useState(undefined);

  const vehicleTypesQuery = useVehicleTypesQuery({
    client: getClient(),
    fetchPolicy: 'network-only',
  });

  const [deleteVehiclePriceCondition] = useDeleteVehiclePriceConditionMutation({
    client: getClient(),
  });

  const vehicleBrandModelsQuery = useGetVehicleBrandModelsQuery({
    client: getClient(),
    fetchPolicy: 'network-only',
  });

  const allModels = [];

  vehicleBrandModelsQuery?.data?.vehicleBrandModels.forEach((brand) => {
    const { models } = brand;
    allModels.push(...models);
  });

  React.useEffect(() => {
    if (addVehiclePriceFilterVisible) {
      setVehiclePriceFilter(undefined);
    }
  }, [addVehiclePriceFilterVisible]);

  return (
    <Modal
      footer={[
        <Button type={'primary'} onClick={props.onClose}>
          Cerrar
        </Button>,
      ]}
      title={`Gestion de precios de ${props.subtype?.name}`}
      width={1000}
      afterClose={props.onClose}
      onCancel={props.onClose}
      onOk={props.onClose}
      visible={props.visible}
      centered={true}
      destroyOnClose={false}
      forceRender={true}
    >
      <Alert
        message='Si el campo "Hora extra" está vacío, se aplican los valores base establecidos en la pestaña "Según vehículo". Si lo desea, puede utilizar un valor distinto para este subtipo de servicio.'
        type="info"
        showIcon
      />
      <div
        style={{
          marginBottom: '2rem',
          marginTop: '2rem',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <h3>Tabla de precios de vehículos</h3>
        <Button
          icon={<LegacyIcon type={'plus'} />}
          onClick={() => {
            setAddVehiclePriceFilterVisible(true);
          }}
        >
          Añadir nuevo
        </Button>
      </div>

      <Table
        size="small"
        columns={[
          {
            title: 'Nombre',
            dataIndex: 'name',
            sorter: (
              a: ServiceSubtype['priceConditions']['vehicle'][0],
              b: ServiceSubtype['priceConditions']['vehicle'][0],
            ) => a.name.localeCompare(b.name),
            defaultSortOrder: 'ascend',
          },
          {
            title: 'Descripción',
            dataIndex: 'description',
          },
          {
            title: 'Tipo',
            key: 'type',
            render: (_: any, record) => {
              if (record.vehicleTypeId) {
                const vehicleType = vehicleTypesQuery.data?.vehicleTypes?.find(
                  (vehicleType) => vehicleType.id === record.vehicleTypeId,
                );

                if (vehicleType) {
                  return vehicleType.name?.toUpperCase();
                }
              }

              return null;
            },
          },
          {
            title: 'Marca',
            key: 'brand',
            render: (_: any, record) => {
              if (record.vehicleBrandId) {
                const vehicleBrand =
                  vehicleBrandModelsQuery.data?.vehicleBrandModels?.find(
                    (vehicleBrand) => vehicleBrand.id === record.vehicleBrandId,
                  );

                if (vehicleBrand) {
                  return vehicleBrand.brand?.toUpperCase();
                }
              }

              return null;
            },
          },
          {
            title: 'Modelo',
            key: 'model',
            render: (_: any, record) => {
              if (record.vehicleModelId) {
                const vehicleModel = allModels?.find(
                  (model) => model.id === record.vehicleModelId,
                );

                if (vehicleModel) {
                  return vehicleModel.name?.toUpperCase();
                }
              }

              return null;
            },
          },
          {
            title: 'Precio base',
            dataIndex: 'basePrice',
            sorter: (
              a: ServiceSubtype['priceConditions']['vehicle'][0],
              b: ServiceSubtype['priceConditions']['vehicle'][0],
            ) => a.basePrice - b.basePrice,
            render: (price: number) => {
              return `${price} €`;
            },
          },
          {
            title: 'Hora extra',
            dataIndex: 'extraHourPrice',
            render: (price: number) => {
              return price > 0 ? `${price} €` : undefined;
            },
          },
          {
            title: 'Capacidad (PAX)',
            dataIndex: 'numPassengers',
            render: (capacity: number) => {
              if (capacity === 0) {
                return null;
              }

              return capacity;
            },
            sorter: (
              a: ServiceSubtype['priceConditions']['vehicle'][0],
              b: ServiceSubtype['priceConditions']['vehicle'][0],
            ) => a.numPassengers || 0 - b.numPassengers || 0,
          },
          {
            key: 'actions',
            width: 100,
            render: (_: any, record) => {
              return (
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <Button
                    icon={<LegacyIcon type={'edit'} />}
                    onClick={() => {
                      setVehiclePriceFilter(record);
                      setModifyVehiclePriceFilterVisible(true);
                    }}
                    style={{ marginRight: '15px' }}
                  >
                    Modificar
                  </Button>
                  <Popconfirm
                    title={'Estás seguro?'}
                    onConfirm={async () => {
                      await deleteVehiclePriceCondition({
                        variables: {
                          id: record.id,
                        },
                      });
                      props.refreshRate();
                    }}
                  >
                    <Button danger>Eliminar</Button>
                  </Popconfirm>
                </div>
              );
            },
          },
        ]}
        dataSource={[...(props.subtype?.priceConditions?.vehicle || [])]}
      />
      <VehiclePriceModal
        key={vehiclePriceFilter?.id}
        clientId={props.clientId}
        rateId={props.rateId}
        serviceSubtypeId={props.subtype?.id}
        vehiclePriceFilter={vehiclePriceFilter}
        visible={
          addVehiclePriceFilterVisible || modifyVehiclePriceFilterVisible
        }
        onClose={() => {
          props.refreshRate();
          setAddVehiclePriceFilterVisible(false);
          setModifyVehiclePriceFilterVisible(false);
        }}
      />
    </Modal>
  );
};
